<script setup lang="ts">
import { useBlog } from '@/services/blog';
import { useUser } from '@/services/user';
import { AppHeaderSearch } from '@teemill/modules/app';

import { useFAQs } from '@/services/faqs';
import { useInstallationConfig } from '@/services/integrations';
import { usePlatform } from '@/services/platform';
import { useProject } from '@/services/projects';
import { captureException } from '@sentry/vue';
import { isHttpError } from '@teemill/modules/utils';
import { computedAsync } from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import TeemillHeaderAccountManagement from './TeemillHeaderAccountManagement.vue';
import TeemillHeaderCart from './TeemillHeaderCart.vue';
import TeemillHeaderNotifications from './TeemillHeaderNotifications.vue';
import TeemillHeaderWebsiteLink from './TeemillHeaderWebsiteLink.vue';
import TeemillThemeIcon from './TeemillThemeIcon.vue';

const isDevelopment = import.meta.env.MODE === 'development';

const user = useUser();
const platform = usePlatform();

const resupplyConfig = useInstallationConfig('resupply');

const showCart = computed(() => !!resupplyConfig.value);

const websiteConfig = useInstallationConfig('website');
const domain = computed(
  () => websiteConfig.value?.customDomainName || websiteConfig.value?.domainName,
);

const coreConfig = useInstallationConfig('core');

const emit = defineEmits<{
  (event: 'blur'): void;
  (event: 'focus'): void;
}>();

const project = useProject();

const blogsList = computedAsync(() => {
  if (!project.value) {
    return [];
  }
  const { blogs } = useBlog();
  return blogs
    .list({
      search: 'visibility:published type:parent-platform,project ',
    })
    .then((res) => res.data.blogs);
});

const faqsList = computedAsync(() => {
  if (!project.value) {
    return [];
  }
  const { faqs } = useFAQs(project.value.id);
  return faqs.list({ search: 'visibility:platform' }).then((res) => res.faqs);
});

const searchQuery = ref('');
const searchResultsLength = ref(0);

watch(searchQuery, (query) => {
  if (query.length < 2 || platform.active.value === undefined) {
    return;
  }
  platform.logSearch(query, searchResultsLength.value, 'platform_search').catch((e) => {
    if (!isHttpError(e) || e.response === undefined) {
      captureException(e);
    }
  });
});
</script>

<template>
  <div class="flex w-full">
    <AppHeaderSearch
      v-model:query="searchQuery"
      v-model:searchResultsLength="searchResultsLength"
      class="hidden sm:block flex-[0.5] mr-4 xs:w-auto"
      :blogs="blogsList"
      :faqs="faqsList"
      :project
      @blur="emit('blur')"
      @focus="emit('focus')"
    />
    <div class="ml-auto flex gap-4">
      <TeemillThemeIcon v-if="isDevelopment" class="!rounded-full hidden sm:block" />
      <component
        :is="action.component"
        v-for="action in coreConfig?.header.actions || []"
        :key="action.name"
      />
      <TeemillHeaderCart v-if="showCart" />
      <Transition name="bounce">
        <TeemillHeaderWebsiteLink v-if="domain" :domain />
      </Transition>
      <TeemillHeaderNotifications v-if="user.active" />
      <TeemillHeaderAccountManagement />
    </div>
  </div>
</template>

<style scoped>
.bounce-enter-active {
  animation: bounce-in 0.5s;
}
.bounce-leave-active {
  animation: bounce-in 0.5s reverse;
}
@keyframes bounce-in {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}
</style>
